/**
 * Hand-authored API types for the Strapi backend integration.
 *
 * This module replaces the previously OpenAPI-generated `api.generated.ts`
 * (which described the legacy backend). It intentionally exposes the same
 * `components` / `operations` / `paths` shapes plus the role value objects the
 * app imports, so the rest of the codebase keeps compiling unchanged.
 */

// ============================================================================
// Roles
// ============================================================================

/**
 * Admin roles. Exposed as a runtime object (for value imports like
 * `UserResponseDtoRole.SUB_ADMIN`) and as a matching string-union type via
 * declaration merging.
 */
export const UserResponseDtoRole = {
  SUPER_ADMIN: "SUPER_ADMIN",
  SUB_ADMIN: "SUB_ADMIN",
  EDITOR: "EDITOR",
} as const;
export type UserResponseDtoRole =
  (typeof UserResponseDtoRole)[keyof typeof UserResponseDtoRole] | (string & {});

// Aliases (kept for parity with the generated enums the app imports).
export const PathsApiUsersGetParametersQueryRole = UserResponseDtoRole;
export type PathsApiUsersGetParametersQueryRole = UserResponseDtoRole;

export const CreateUserDtoRole = UserResponseDtoRole;
export type CreateUserDtoRole = UserResponseDtoRole;

export const UpdateUserDtoRole = UserResponseDtoRole;
export type UpdateUserDtoRole = UserResponseDtoRole;

export const AssignRoleDtoRole = UserResponseDtoRole;
export type AssignRoleDtoRole = UserResponseDtoRole;

export const InviteUserDtoRole = UserResponseDtoRole;
export type InviteUserDtoRole = UserResponseDtoRole;

export const SYSTEM_ROLES = [
  UserResponseDtoRole.SUPER_ADMIN,
  UserResponseDtoRole.SUB_ADMIN,
  UserResponseDtoRole.EDITOR,
] as const;
export type SystemRole = (typeof SYSTEM_ROLES)[number];

// ============================================================================
// Filter / query enums (type-only)
// ============================================================================

export type PathsApiReportsExportGetParametersQueryReportType =
  | "content"
  | "users"
  | "submissions";
export type PathsApiReportsExportGetParametersQueryFormat = "csv" | "xlsx";

export type PathsApiContentGetParametersQueryStatus =
  | "draft"
  | "published"
  | "pending"
  | "approved"
  | "rejected";
export type PathsApiContentExportGetParametersQueryStatus =
  PathsApiContentGetParametersQueryStatus;

export type PathsApiSubmissionsGetParametersQueryType =
  | "contact"
  | "complaint"
  | "suggestion"
  | "inquiry"
  | "other";

// ============================================================================
// Schema DTOs
// ============================================================================

export type DepartmentAccessType = 'ALL' | 'RESTRICTED';

export interface DepartmentAccessDto {
  type: DepartmentAccessType;
  departmentIds: string[];
}

export interface UserResponseDto {
  id: string;
  email: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  role: UserResponseDtoRole;
  isActive: boolean;
  isEmailVerified: boolean;
  mustChangePassword?: boolean;
  departmentId?: string | null;
  homeDepartmentId?: string | null;
  subAdminDepartmentIds?: string[];
  cmsDepartmentAccess?: DepartmentAccessDto;
  /** Users, reports, audit, documents governance — from /api/auth/me */
  governanceDepartmentAccess?: DepartmentAccessDto;
  ticketingDepartmentAccess?: DepartmentAccessDto;
  /** CMS data-scope allow-list from /api/auth/me (empty when unrestricted). */
  departmentIds?: string[];
  lastLoginAt?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateUserDto {
  email: string;
  username?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  role: CreateUserDtoRole;
  isActive?: boolean;
  departmentId?: string;
  subAdminDepartmentIds?: string[];
}

/** POST /api/users response — temporaryPassword is returned once at creation. */
export interface CreateUserResponseDto extends UserResponseDto {
  temporaryPassword?: string;
}

export interface UpdateUserDto {
  email?: string;
  username?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  role?: UpdateUserDtoRole;
  isActive?: boolean;
  isEmailVerified?: boolean;
  departmentId?: string | null;
  subAdminDepartmentIds?: string[];
  bio?: string;
  language?: string;
  timezone?: string;
}

export interface AssignRoleDto {
  role: AssignRoleDtoRole;
}

export interface InviteUserDto {
  email: string;
  username: string;
  role: InviteUserDtoRole;
  firstName?: string;
  lastName?: string;
  departmentId?: string;
  subAdminDepartmentIds?: string[];
}

// ---- Auth ------------------------------------------------------------------

export interface LoginDto {
  identifier: string;
  /** @deprecated Use `identifier` instead */
  email?: string;
  password: string;
  redirectTo?: string;
}

export interface RegisterDto {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}

export interface VerifyEmailDto {
  token: string;
}

export interface VerifyOtpDto {
  email?: string;
  /** @deprecated Use `email` (resolved from login response) */
  identifier?: string;
  otp?: string;
  code?: string;
  token?: string;
}

export interface ForgotPasswordDto {
  email: string;
}

export interface ResetPasswordDto {
  token: string;
  password: string;
}

export interface CompleteInitialPasswordDto {
  newPassword: string;
}

export interface ResendVerificationDto {
  email: string;
}

export interface OtpInitiatedResponseDto {
  email?: string;
  message?: string;
  otpRequired?: boolean;
  requiresOtp?: boolean;
  expiresIn?: string;
}

export interface AuthResponseDto {
  accessToken?: string;
  expiresIn?: string;
  redirectTo?: string;
  user?: UserResponseDto;
}

export interface RefreshTokenDataDto {
  accessToken?: string;
  expiresIn?: string;
}

export interface WrappedResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export type WrappedResponseAuthResponseDto = WrappedResponse<AuthResponseDto>;
export type WrappedResponseOtpInitiatedResponseDto =
  WrappedResponse<OtpInitiatedResponseDto>;
export type WrappedResponseRefreshTokenDataDto =
  WrappedResponse<RefreshTokenDataDto>;

// ---- Content ---------------------------------------------------------------

export interface RejectContentDto {
  note?: string;
  reason?: string;
}

// ---- Settings --------------------------------------------------------------

export interface PlatformSettingsDto {
  siteName?: string;
  supportEmail?: string;
  defaultLocale?: string;
  [key: string]: unknown;
}

export interface SecuritySettingsDto {
  sessionTimeoutMinutes?: number;
  maxLoginAttempts?: number;
  lockoutDurationMinutes?: number;
  [key: string]: unknown;
}

export interface UpdateSettingsDto {
  platform?: PlatformSettingsDto;
  security?: SecuritySettingsDto;
  emailFrom?: string;
  allowedOrigins?: string[];
  maintenanceMode?: boolean;
  maintenanceMessage?: string;
}

export interface ToggleMaintenanceDto {
  enabled: boolean;
  message?: string;
}

// ---- Integrations ----------------------------------------------------------

export interface UpdateIntegrationDto {
  isActive?: boolean;
  apiKey?: string;
  config?: Record<string, unknown>;
  [key: string]: unknown;
}

// ---- Documents -------------------------------------------------------------

export interface UpdateDocumentPermissionsDto {
  permissions?: Record<string, unknown>;
  roles?: string[];
  [key: string]: unknown;
}

// ---- Backups ---------------------------------------------------------------

export interface CreateBackupDto {
  type: string;
  notes?: string;
}

export interface RestoreBackupDto {
  confirm?: boolean;
}

// ---- Citizen budget --------------------------------------------------------

export interface UpdateVisualizationDto {
  visualConfig?: Record<string, unknown>;
  [key: string]: unknown;
}

// ============================================================================
// components — mirrors the generated `components["schemas"][...]` indexing.
// ============================================================================

export interface components {
  schemas: {
    UserResponseDto: UserResponseDto;
    CreateUserDto: CreateUserDto;
    CreateUserResponseDto: CreateUserResponseDto;
    UpdateUserDto: UpdateUserDto;
    AssignRoleDto: AssignRoleDto;
    InviteUserDto: InviteUserDto;
    LoginDto: LoginDto;
    RegisterDto: RegisterDto;
    VerifyEmailDto: VerifyEmailDto;
    VerifyOtpDto: VerifyOtpDto;
    ForgotPasswordDto: ForgotPasswordDto;
    ResetPasswordDto: ResetPasswordDto;
    CompleteInitialPasswordDto: CompleteInitialPasswordDto;
    ResendVerificationDto: ResendVerificationDto;
    OtpInitiatedResponseDto: OtpInitiatedResponseDto;
    AuthResponseDto: AuthResponseDto;
    RefreshTokenDataDto: RefreshTokenDataDto;
    WrappedResponseAuthResponseDto: WrappedResponseAuthResponseDto;
    WrappedResponseOtpInitiatedResponseDto: WrappedResponseOtpInitiatedResponseDto;
    WrappedResponseRefreshTokenDataDto: WrappedResponseRefreshTokenDataDto;
    RejectContentDto: RejectContentDto;
    PlatformSettingsDto: PlatformSettingsDto;
    SecuritySettingsDto: SecuritySettingsDto;
    UpdateSettingsDto: UpdateSettingsDto;
    ToggleMaintenanceDto: ToggleMaintenanceDto;
    UpdateIntegrationDto: UpdateIntegrationDto;
    UpdateDocumentPermissionsDto: UpdateDocumentPermissionsDto;
    CreateBackupDto: CreateBackupDto;
    RestoreBackupDto: RestoreBackupDto;
    UpdateVisualizationDto: UpdateVisualizationDto;
  };
}

// ============================================================================
// operations — mirrors `operations[...]["parameters"]["query"]` indexing.
// ============================================================================

interface PaginationQuery {
  page?: number;
  limit?: number;
}

interface UsersListQuery extends PaginationQuery {
  search?: string;
  role?: UserResponseDtoRole;
  departmentId?: string;
  isActive?: boolean;
}

interface ContentListQuery {
  contentType?: string;
  status?: PathsApiContentGetParametersQueryStatus;
  departmentId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  unassigned?: boolean;
}

interface ContentExportQuery {
  contentType: string;
  status?: PathsApiContentExportGetParametersQueryStatus;
  departmentId?: string;
}

interface DocumentsListQuery extends PaginationQuery {
  departmentId?: string;
  search?: string;
}

interface AuditLogsQuery extends PaginationQuery {
  userId?: string;
  action?: string;
  resource?: string;
  departmentId?: string;
  dateFrom?: string;
  dateTo?: string;
}

interface SubmissionsQuery extends PaginationQuery {
  type?: PathsApiSubmissionsGetParametersQueryType;
  departmentId?: string;
  search?: string;
}

interface ReportFilterQuery {
  dateFrom?: string;
  dateTo?: string;
  departmentId?: string;
}

interface ReportExportQuery extends ReportFilterQuery {
  reportType: PathsApiReportsExportGetParametersQueryReportType;
  format: PathsApiReportsExportGetParametersQueryFormat;
}

export interface operations {
  UsersController_list: { parameters: { query?: UsersListQuery } };
  UsersController_activity: { parameters: { query?: PaginationQuery } };
  ContentController_list: { parameters: { query?: ContentListQuery } };
  ContentController_export: { parameters: { query?: ContentExportQuery } };
  DocumentsController_list: { parameters: { query?: DocumentsListQuery } };
  CitizenBudgetController_listDatasets: {
    parameters: { query?: PaginationQuery };
  };
  BackupsController_list: { parameters: { query?: PaginationQuery } };
  AuditLogsController_list: { parameters: { query?: AuditLogsQuery } };
  AuditLogsController_export: { parameters: { query?: AuditLogsQuery } };
  SubmissionsController_list: { parameters: { query?: SubmissionsQuery } };
  SubmissionsController_export: { parameters: { query?: SubmissionsQuery } };
  ReportsController_exportReport: { parameters: { query?: ReportExportQuery } };
  ReportsController_contentReport: {
    parameters: { query?: ReportFilterQuery };
  };
  ReportsController_usersReport: { parameters: { query?: ReportFilterQuery } };
  ReportsController_submissionsReport: {
    parameters: { query?: ReportFilterQuery };
  };
}

// ============================================================================
// paths — only used at the type level (`keyof paths`) to constrain endpoints.
// ============================================================================

export interface paths {
  "/api/audit-logs": unknown;
  "/api/audit-logs/export": unknown;
  "/api/backups": unknown;
  "/api/backups/schedule": unknown;
  "/api/citizen-budget/datasets": unknown;
  "/api/citizen-budget/datasets/upload": unknown;
  "/api/citizen-budget/pdf-reports/upload": unknown;
  "/api/content": unknown;
  "/api/content/export": unknown;
  "/api/departments": unknown;
  "/api/documents": unknown;
  "/api/documents/upload": unknown;
  "/api/reports/content": unknown;
  "/api/reports/export": unknown;
  "/api/reports/submissions": unknown;
  "/api/reports/users": unknown;
  "/api/settings": unknown;
  "/api/settings/maintenance": unknown;
  "/api/submissions": unknown;
  "/api/submissions/export": unknown;
  "/api/users": unknown;
  "/api/users/invite": unknown;
  "/api/auth/login": unknown;
  "/api/auth/verify-otp": unknown;
  "/api/auth/register": unknown;
  "/api/auth/refresh": unknown;
  "/api/auth/verify-email": unknown;
  "/api/auth/logout": unknown;
  "/api/auth/me": unknown;
  "/api/auth/forgot-password": unknown;
  "/api/auth/reset-password": unknown;
  "/api/auth/resend-verification": unknown;
}
